import { memo, useMemo } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination } from '../../../components/common/Pagination';
import {
  TableSkeleton,
  type CellAlign,
  type SkeletonVariant,
  type TableSkeletonColumn,
} from '../../../components/common/TableSkeleton/TableSkeleton';

/**
 * Static (render-independent) description of one table column. Each table
 * declares a single `as const` array of these; the table body and its
 * loading skeleton are both derived from it, so they can never drift.
 */
export interface DataTableColumnMeta {
  key: string;
  /** i18n key for the header label (shared with the skeleton). */
  labelKey: string;
  width: string;
  align?: CellAlign;
  /** Skeleton bar style for this column while loading. */
  skeletonVariant?: SkeletonVariant;
  /** Extra class(es) for body cells (e.g. 'cell-id', 'cell-price'). */
  cellClassName?: string;
  /** Render digits with equal widths (currency, counters). */
  tabularNums?: boolean;
}

/** One render function per column key, typed against the meta array. */
export type DataTableRenderers<T, C extends readonly DataTableColumnMeta[]> = Record<
  C[number]['key'],
  (row: T) => ReactNode
>;

export interface DataTablePaginationProps {
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

interface DataTableProps<T, C extends readonly DataTableColumnMeta[]> {
  columns: C;
  renderers: DataTableRenderers<T, C>;
  rows: readonly T[];
  rowKey: (row: T) => string;
  /** Omit for unpaginated tables (pagination may also live outside). */
  pagination?: DataTablePaginationProps;
  /**
   * Width below which the table scrolls horizontally instead of squeezing its
   * columns into each other (e.g. '1180px').
   */
  minWidth?: string;
  /** Tighter cells and status pills, for tables that would not otherwise fit. */
  dense?: boolean;
  /** Makes rows clickable and keyboard-activatable. */
  onRowClick?: (row: T) => void;
  /** Accessible name for clickable rows; required with `onRowClick`. */
  rowAriaLabel?: (row: T) => string;
}

const headerAlignClass = (a?: CellAlign) =>
  a === 'center' ? ' table-header-cell-center'
  : a === 'right' ? ' table-header-cell-right'
  : '';

const cellAlignClass = (a?: CellAlign) =>
  a === 'center' ? ' table-cell-center'
  : a === 'right' ? ' table-cell-right'
  : '';

const TABULAR_NUMS = { fontVariantNumeric: 'tabular-nums' } as const;

/**
 * Config-driven desktop table shared by the Dashboard V2 list pages: one
 * column-meta array drives the header, the body cells, and (via
 * `DataTableSkeleton`) the loading state.
 */
export const DataTable = memo(function DataTable<
  T,
  C extends readonly DataTableColumnMeta[],
>({
  columns,
  renderers,
  rows,
  rowKey,
  pagination,
  minWidth,
  dense,
  onRowClick,
  rowAriaLabel,
}: DataTableProps<T, C>) {
  const { t } = useTranslation();

  const rowProps = (row: T) =>
    onRowClick
      ? {
          className: 'table-body-row clickable',
          onClick: () => onRowClick(row),
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onRowClick(row);
            }
          },
          tabIndex: 0,
          role: 'button',
          'aria-label': rowAriaLabel?.(row),
        }
      : { className: 'table-body-row' };

  return (
    <div className="stations-table-wrapper">
      <div className="stations-table-scroll">
        <table
          className={`stations-table${dense ? ' stations-table-dense' : ''}`}
          style={minWidth ? { minWidth } : undefined}
        >
          <thead>
            <tr className="table-header-row">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`table-header-cell${headerAlignClass(col.align)}`}
                  style={{ width: col.width }}
                  scope="col"
                >
                  <span>{t(col.labelKey)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)} {...rowProps(row)}>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`table-cell${cellAlignClass(col.align)}${
                      col.cellClassName ? ` ${col.cellClassName}` : ''
                    }`}
                    style={col.tabularNums ? TABULAR_NUMS : undefined}
                  >
                    {(renderers as Record<string, (row: T) => ReactNode>)[col.key](row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && (
        <Pagination
          total={pagination.total}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={pagination.onPageChange}
          onRowsPerPageChange={pagination.onRowsPerPageChange}
        />
      )}
    </div>
  );
}) as <T, C extends readonly DataTableColumnMeta[]>(props: DataTableProps<T, C>) => ReactNode;

interface DataTableSkeletonProps {
  columns: readonly DataTableColumnMeta[];
  rows?: number;
  /** Show the spinning "Loading Data" overlay (used by Stations). */
  showLoadingOverlay?: boolean;
  /** Must match the table's `minWidth` so the skeleton has the same columns. */
  minWidth?: string;
  /** Must match the table's `dense`. */
  dense?: boolean;
}

/** Loading skeleton derived from the same column meta as the table. */
export const DataTableSkeleton = memo(function DataTableSkeleton({
  columns,
  rows = 5,
  showLoadingOverlay,
  minWidth,
  dense,
}: DataTableSkeletonProps) {
  const { t } = useTranslation();
  const skeletonColumns = useMemo<TableSkeletonColumn[]>(
    () =>
      columns.map((col) => ({
        label: t(col.labelKey),
        width: col.width,
        align: col.align,
        variant: col.skeletonVariant,
      })),
    [columns, t],
  );

  return (
    <TableSkeleton
      columns={skeletonColumns}
      rows={rows}
      showLoadingOverlay={showLoadingOverlay}
      minWidth={minWidth}
      dense={dense}
    />
  );
});

export default DataTable;
